import clsx from 'clsx'
import { replace } from 'lib/routing/appRouting'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import type { SearchFormRef } from 'components/Search'
import { mediaQueryUp } from 'components/utils/breakpoint'
import {
  ALL_HOTEL_TYPES,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import {
  decodeSearchParams,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchTypeFromFilters,
} from 'lib/search/searchUtils'
import { serialiseOccupancy } from 'lib/search/occupancy'
import { take } from 'lib/array/arrayUtils'
import {
  deleteSearchParams,
  parseSearchString,
} from 'lib/url/searchUrlUtils'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'

import type { RankingPreferences } from '@luxuryescapes/contract-search'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import { addToMetasearchBlacklist } from 'actions/MarketingAction'
import { fetchMetasearchDiscount } from 'actions/MetasearchDiscountActions'
import { fetchMetaSearchOfferRates } from 'actions/OfferActions'
import { trackClientEvent, trackEvent } from 'analytics/analytics'
import { getListName } from 'analytics/enhanced-ecommerce'
import { impressionEventWithContext, productClickEventWithContext, searchEvent } from 'analytics/snowplow/events'
import { CompactFooter, OriginalFooter } from 'components/App/Footer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import HttpStatus from 'components/Common/HttpStatus'
import LayoutContainer from 'components/Common/LayoutContainer'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferListFilterSidebar from 'components/OfferList/OfferListFilter/OfferListFilterSidebar'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import OfferListLoadMoreButton from 'components/OfferList/OfferListLoadMoreButton'
import OfferListLoadingOfferTile from 'components/OfferList/OfferListTiles/OfferListLoadingOfferTile'
import OfferListOfferTile from 'components/OfferList/OfferListTiles/OfferListOfferTile'
import type {
  FilterControlChangeHandler,
} from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import MetaSearchPageHeadData from 'components/Pages/HotelSearchPage/MetaSearchPageHeadData'
import MetaSearchPageWarmupHints from 'components/Pages/HotelSearchPage/MetaSearchPageWarmupHints'
import MetaSearchV2Header from 'components/Pages/HotelSearchPage/MetaSearchV2/MetaSearchV2Header'
import type { OfferListSortOption } from 'constants/offerListFilters'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import config from 'constants/config'
import { Experiments } from 'constants/experiments'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import useMetaSearchOfferList from 'hooks/Offers/useMetaSearchOfferList'
import useOffer from 'hooks/Offers/useOffer'
import useOfferList from 'hooks/Offers/useOfferList'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import useSearchOfferTileDensityExperiment from 'hooks/Search/useSearchOfferTileDensityExperiment'
import { shouldEnableSearchOfferTileDensityExperimentOnMetaSearch } from 'hooks/Search/searchOfferTileDensityPriority'
import useQueryParams from 'hooks/useQueryParams'
import useMetasearchPriceId from 'hooks/useMetasearchPriceId'
import useMetasearchRuleId from 'hooks/useMetasearchRuleId'
import { getHotelEnabledFilters } from 'selectors/filters/filterSelectors'
import { buildMetaSearchObjectFromOffer, buildMetaSearchOfferListFilters, buildYmalTileFilters, getMetaSearchPrioritySecondaryTileCount } from 'lib/metasearch/metaSearchOfferListFilters'
import { getMetasearchDateFilters } from 'lib/metasearch/metasearchDateFilters'
import { isEligibleMetasearchCugRateRule } from 'lib/metasearch/metasearchRateRules'
import { pickKeys } from 'lib/object/objectUtils'
import getOfferListKey from 'lib/offer/offerListKey'
import { isBedbankOffer, isFlashHotelType, isHotel } from 'lib/offer/offerTypes'
import { generalLog } from 'marketing/api'
import VoucherDisplayConfig from 'marketing/components/VoucherDisplayConfig'
import { useIntl } from 'react-intl'
import { getSourceForProperty } from 'selectors/offerSelectors'
import { reportLog } from 'services/userMonitoring'
import Group from '../../utils/Group'
import Error404PageWithOffers from '../Error404PageWithOffers/Error404PageWithOffers'
import HotelSearchHeader from './HotelSearchHeader'
import useMetaSearchMobileV2, { MetaSearchMobileV2Context } from './useMetaSearchMobileV2'
import useProgressiveYmalTileCount, { METASEARCH_LOAD_MORE_BATCH_SIZE } from './useProgressiveYmalTileCount'

const YMAL_LIMIT = 20
const MAIN_OFFER_LIMIT = 1
const METASEARCH_DISTANCE_FUNCTION_SCORE_WEIGHTS: RankingPreferences = {
  distanceImportance: 1,
  offerScoreImportance: 0,
  offsetMeters: 0,
}

/** Filters applied via nearby stream (YMAL cannot). */
const META_NEARBY_LISTING_FILTER_KEYS = [
  'amenities',
  'holidayTypes',
  'inclusions',
  'customerRatingGte',
  'priceLte',
  'locations',
  'propertyTypes',
  'bedroomsGte',
  'bedroomsEq',
  'bedsGte',
  'bedsEq',
  'bathroomsGte',
  'bathroomsEq',
  'luxPlusFeatures',
  'campaigns',
  'chains',
  'paymentOptions',
] as const satisfies ReadonlyArray<keyof App.OfferListFilters>

function hasMetaNearbyListingFilters(filters: App.OfferListFilters): boolean {
  return META_NEARBY_LISTING_FILTER_KEYS.some((key) => {
    const value = filters[key]
    return Array.isArray(value) ? value.length > 0 : value != null
  })
}

type MetaSearchListSortMode = 'recommendation' | 'distance' | 'discount' | 'rating'

const META_LIST_SORT_MODES = new Set<string>(['recommendation', 'distance', 'discount', 'rating'])

/** Nearby stream API sortBy — discount has no equivalent. */
const NEARBY_STREAM_SORT_BY: Partial<Record<MetaSearchListSortMode, App.OfferListSortByOptions>> = {
  distance: 'distance',
  rating: 'customerRating',
  recommendation: 'recommended',
}

function resolveMetaListSortMode(
  rawSortBy: string | null,
  isVerifier: boolean,
): MetaSearchListSortMode {
  if (isVerifier) return 'recommendation'
  // OfferSortControl writes `recommended` (or deletes sortBy) — map to meta mode.
  if (rawSortBy === 'recommended') return 'recommendation'
  if (rawSortBy && META_LIST_SORT_MODES.has(rawSortBy)) return rawSortBy as MetaSearchListSortMode
  return 'recommendation'
}

function shouldShowNearbyOffer(
  offerId: string,
  flashOfferId: string | undefined,
  offerMetaData: App.OfferListMetaData | undefined,
) {
  return (
    offerId === flashOfferId ||
    !offerMetaData?.isMatchingProperty ||
    isFlashHotelType(offerMetaData.type as App.OfferType | undefined)
  )
}

const META_SEARCH_V2_PAGE_BACKGROUND = '#FCFCFC'

const PageWrapper = styled(Group)`
  height: calc(var(--vh, 1vh) * 100);

  &.v2 {
    /* min-height so the document can scroll under the sticky V2 header. */
    height: auto;
    min-height: calc(var(--vh, 1vh) * 100);
    background-color: ${META_SEARCH_V2_PAGE_BACKGROUND};
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding-top: ${rem(24)};
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;

    &.v2 {
      padding: 0;
    }
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
  margin-top: ${rem(16)};

  &.v2 {
    margin-top: 0;
    padding: ${rem(12)} ${rem(12)} ${rem(16)};
    background-color: ${META_SEARCH_V2_PAGE_BACKGROUND};

    > .offer-tile {
      border-radius: ${props => props.theme.borderRadius.L};
      background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
      box-shadow: ${props => props.theme.shadow.bottom.small};
      overflow: hidden;
    }
  }
`

const MetaSearchLoadMoreButton = styled(OfferListLoadMoreButton)`
  margin-top: ${rem(4)};
`

const MetaSearchV2SaveUpToBanner = styled(MessageBanner)`
  border-radius: ${props => props.theme.borderRadius.L};
`

function mapState(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    pathName: state.router.location.pathname,
    filters,
    memberId: state.auth.account.memberId,
    searchResultMetadata: state.offer.searchResultMetadata,
    isFlexibleSearch: !!(filters?.flexibleMonths && filters?.flexibleNights),
    specificPlaces: state.destination.specificPlaces[filters.destinationId ?? ''],
    region: state.geo.currentRegionCode,
    currency: state.geo.currentCurrency,
  }
}

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux {}

function MetaSearchPage({
  filters,
  searchResultMetadata,
  windowSearch,
  pathName,
  region,
  currency,
}: Props) {
  const intl = useIntl()
  const navigate = useNavigate()
  const { landmarkSearchSortByTypes, searchSortByTypes, SORT_OPTION_CUSTOMER_RATING } = useOfferListSortOptions()
  const queryParams = useQueryParams()
  const { propertyId = '' } = useParams<'propertyId'>()
  const dispatch = useAppDispatch()
  const isMobile = useScreenSizeOnly('mobile')
  const isMobileDevice = useIsMobileDevice()
  const prioritySecondaryTileCount = getMetaSearchPrioritySecondaryTileCount(isMobileDevice)
  const isMetaSearchMobileV2 = useMetaSearchMobileV2(true)
  const searchType = getSearchTypeFromFilters(filters)
  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchParams = useMemo(() => decodeSearchParams(windowSearch), [windowSearch])
  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])
  const utmBp = useAppSelector(state => state.utm.bp)
  const utmContent = useAppSelector(state => state.utm.content)
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const topOfferSource = useAppSelector(state => getSourceForProperty(state, propertyId))
  const flashOfferId = useAppSelector(state =>
    state.offer.alternativeOffersForProperty[propertyId]?.leOfferDetails.find(
      detail => detail.type === OFFER_TYPE_HOTEL,
    )?.offerId,
  )
  const storedRuleId = useAppSelector(state => state.utm.ruleId)
  const gpid = useMetasearchPriceId()
  const rateRuleId = useMetasearchRuleId()
  const hasEligibleGHACugRule = utmBp === '3' && isEligibleMetasearchCugRateRule(storedRuleId)
  const shouldApplyCugBlacklist = utmBp === '3' && !hasEligibleGHACugRule

  // Blacklist this property so it retains B2C rates if the user navigates to its /partner/ page
  // or encounters it on search results later within the TTL window.
  // Skip when eligible ruleId is present — the ruleId override forces CUG and the
  // blacklist is irrelevant.
  useEffect(() => {
    if (shouldApplyCugBlacklist && propertyId) {
      dispatch(addToMetasearchBlacklist(propertyId))
    }
  }, [dispatch, shouldApplyCugBlacklist, propertyId])
  const userAgent = useAppSelector(state => state.system.rawUserAgentString)
  const isGoogleHotelAdsVerifier = userAgent.includes('Google-HotelAdsVerifier')
  const listSortMode = resolveMetaListSortMode(
    queryParams.get('sortBy'),
    isGoogleHotelAdsVerifier,
  )
  const { isFilterPanelOpen, toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const enabledFilters = useAppSelector(getHotelEnabledFilters)
  const {
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  } = useMemo(() => ({
    searchTargetLabel: params.landmarkName || params.destinationName || params.propertyName,
    offerDistanceFromSearchTarget: searchResultMetadata.distanceFromSearchTarget[params.landmarkId || params.destinationId || ''],
  }), [
    params.landmarkId,
    params.landmarkName,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    searchResultMetadata.distanceFromSearchTarget,
  ])

  const formRef = useRef<SearchFormRef>(null)
  const hideSearchHeader = queryParams.get('hide_search') === 'true'
  const canActivatePerNightPricing = config.BRAND === 'luxuryescapes' && (utmContent === 'gha-lpp' || (utmSource === 'google' && utmMedium === 'cpc'))
  const { isAnyVariant: isPerNightPricingVariant } = useExperiment(
    Experiments.growthMetasearchPerNightPricing,
    { canActivate: canActivatePerNightPricing },
  )
  const shouldShowPerNightPricing = canActivatePerNightPricing && isPerNightPricingVariant
  const luxuryHotelFallbackName = intl.formatMessage({
    id: 'HKJViPBpFCTQAQ6DQ151',
    defaultMessage: 'Luxury Hotel',
    description: 'Fallback property name used on the meta search page when a hotel offer name is unavailable or not a hotel',
  })

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    searchTargetLabel,
    offerDistanceFromSearchTarget,
  }, queryParams)

  const metasearchFilters = useMemo(
    () => getMetasearchDateFilters(filters, windowSearch),
    [filters, windowSearch],
  )

  const bypassRegionSettingId = useAppSelector(state => state.marketing.bypassRegionSettingId)
  const listFilters = useMemo((): App.OfferListFilters => {
    return {
      ...metasearchFilters,
      offerTypes: [...ALL_HOTEL_TYPES],
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      source: topOfferSource,
      propertyId,
      limit: MAIN_OFFER_LIMIT,
      // Must mirror fetchMetaSearchOffer.ts exactly — offerListKey drift causes the SSR'd
      // list to be ignored at hydration (silent re-fetch).
      ...(bypassRegionSettingId ? { rollout: ['includeAllRegionsLOS'] } : {}),
    }
  }, [metasearchFilters, searchType, topOfferSource, propertyId, bypassRegionSettingId])
  const offerListKey = useMemo(() => getOfferListKey(listFilters), [listFilters])
  const topOfferPricing = searchResultMetadata.offerMetaData[offerListKey]?.[propertyId]?.pricing
  const topOfferPrice = topOfferPricing?.lowestPrice
  const topOfferRateId = topOfferPricing?.lowestPriceRoomRateId
  const {
    density: searchTileDensity,
    isCompact: isSearchTileDensityExperimentCompact,
  } = useSearchOfferTileDensityExperiment(listFilters, {
    // V2 owns metasearch mobile layout; do not mix Optimizely denser tiles in.
    enabled: shouldEnableSearchOfferTileDensityExperimentOnMetaSearch(isMetaSearchMobileV2),
  })
  const secondarySearchTileDensity = isSearchTileDensityExperimentCompact ? 'default' : searchTileDensity
  const mainOfferSearchTileTreatment = isSearchTileDensityExperimentCompact ? 'featured-match' : 'default'

  const [offer, fetching] = useOffer(propertyId, {
    requireSummaryOnly: true,
  })
  const [propertyMapping, fetchingPropertyMapping] = usePropertyOfferMapping(OFFER_TYPE_BED_BANK, propertyId)

  useEffect(() => {
    const nextQueryParams = new URLSearchParams(queryParams)
    const childrenQueryParam = queryParams.get('children')
    const adultQueryParam = queryParams.get('adults')
    const propertyNameQueryParam = queryParams.get('propertyName')
    const propertyIdPrefix = isBedbankOffer(offer) ? 'bedbank' : 'le'
    const currentSortBy = queryParams.get('sortBy')
    if (listSortMode !== currentSortBy && currentSortBy !== null) {
      nextQueryParams.set('sortBy', listSortMode)
    }
    if (!childrenQueryParam?.trim()) {
      nextQueryParams.set('children', 'none')
    }
    if (!adultQueryParam || adultQueryParam === '') {
      nextQueryParams.set('adults', '2')
    }
    nextQueryParams.set('propertyId', `${propertyIdPrefix}:${propertyId}`)
    if (!propertyNameQueryParam) {
      const propertyName = isHotel(offer) ? offer.property?.name : luxuryHotelFallbackName
      nextQueryParams.set('propertyName', propertyName)
    }

    const nextSearch = nextQueryParams.toString()
    // Canonicalise before comparing: other writers serialise this URL with qs
    // (%20 for spaces) while URLSearchParams uses '+'. Comparing raw strings makes
    // encoding-only differences replace() against those writers in a visible loop.
    const currentSearch = new URLSearchParams(windowSearch).toString()

    if (nextSearch === currentSearch) {
      return
    }

    navigate({ search: nextSearch }, { replace: true })
  }, [navigate, queryParams, propertyId, offer, listSortMode, luxuryHotelFallbackName, windowSearch])

  const metaSearchFilters = useMemo(
    () => buildMetaSearchObjectFromOffer(offer, region, metasearchFilters, { limit: YMAL_LIMIT }),
    [offer, region, metasearchFilters],
  )
  // Must match the YMAL list key the SSR fetcher writes to (see
  // `fetchMetaSearchOffer.ts`). It lets warm-up hints read the SSR-populated ID
  // list (with flash at index 0) and resets progressive tiles when that list changes.
  const ymalListKey = useMemo(
    () => metaSearchFilters
      ? getOfferListKey(buildMetaSearchOfferListFilters(metaSearchFilters))
      : undefined,
    [metaSearchFilters],
  )

  // YMAL ignores listing filters — on V2, switch to the nearby stream when any
  // are set. V1 control keeps master’s distance-only nearby streaming + YMAL fetch.
  const hasListingFilters = isMetaSearchMobileV2 && hasMetaNearbyListingFilters(metasearchFilters)
  const isDistanceMode = listSortMode === 'distance'
  const isNearbyStreaming = isDistanceMode || hasListingFilters

  const {
    items: metaSearchOfferList,
    fetching: metaSearchOfferListFetchingFromHook,
  } = useMetaSearchOfferList(metaSearchFilters, {
    // Skip for bots (main tile only). On V2 also skip when nearby stream is active.
    disabled: !offer || isGoogleHotelAdsVerifier || (isMetaSearchMobileV2 && isNearbyStreaming),
    sortMode: listSortMode,
    propertyId,
  })

  const metaSearchOfferListFetching = isBedbankOffer(offer) && (
    fetching || metaSearchOfferListFetchingFromHook
  )
  const [ymalTileCount, onYmalLoadMore] = useProgressiveYmalTileCount(
    ymalListKey,
    listSortMode,
    prioritySecondaryTileCount,
    metaSearchOfferList.length,
  )

  const discountOccupancy = useMemo(
    () => metasearchFilters.rooms ? serialiseOccupancy(metasearchFilters.rooms) : undefined,
    [metasearchFilters.rooms],
  )
  const hasTopOfferSelectedDates = !!listFilters.checkIn && !!listFilters.checkOut
  const topOfferQueryParams = useMemo(() => Object.fromEntries(queryParams.entries()), [queryParams])
  const topOfferLogKey = useMemo(() => JSON.stringify({
    userAgent,
    region,
    price: topOfferPrice,
    rateId: topOfferRateId,
    propertyId,
    checkIn: listFilters.checkIn,
    checkOut: listFilters.checkOut,
    occupancy: discountOccupancy,
    queryParams: topOfferQueryParams,
  }), [discountOccupancy, listFilters.checkIn, listFilters.checkOut, propertyId, region, topOfferPrice, topOfferQueryParams, topOfferRateId, userAgent])
  const loggedTopOfferKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!hasTopOfferSelectedDates || !topOfferPricing || topOfferPrice === undefined || loggedTopOfferKeyRef.current === topOfferLogKey) {
      return
    }

    loggedTopOfferKeyRef.current = topOfferLogKey
    reportLog('Metasearch top offer shown', {
      userAgent,
      region,
      price: topOfferPrice,
      rateId: topOfferRateId,
      propertyId,
      checkIn: listFilters.checkIn,
      checkOut: listFilters.checkOut,
      occupancy: discountOccupancy,
      queryParams: topOfferQueryParams,
    }, 'info')
  }, [discountOccupancy, hasTopOfferSelectedDates, listFilters.checkIn, listFilters.checkOut, propertyId, region, topOfferLogKey, topOfferPrice, topOfferPricing, topOfferQueryParams, topOfferRateId, userAgent])

  const secondarySearchPropertyId = propertyMapping?.propertyId ?? `bedbank:${propertyId}`

  const distanceSearchFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!isBedbankOffer(offer) || !propertyId) return undefined
    const sortBy = isMetaSearchMobileV2 && hasListingFilters
      ? NEARBY_STREAM_SORT_BY[listSortMode]
      : undefined
    return {
      propertyId: secondarySearchPropertyId,
      isStream: true,
      offerTypes: [...ALL_HOTEL_TYPES],
      checkIn: isNearbyStreaming ? metasearchFilters.checkIn : undefined,
      checkOut: isNearbyStreaming ? metasearchFilters.checkOut : undefined,
      rooms: isNearbyStreaming ? metasearchFilters.rooms : undefined,
      limit: YMAL_LIMIT,
      include: ['nearby'],
      showDistanceWithMetaOfferId: propertyId,
      ...(sortBy ? { sortBy } : {}),
      ...(isMetaSearchMobileV2 ? pickKeys(META_NEARBY_LISTING_FILTER_KEYS, metasearchFilters) : {}),
    }
  }, [
    offer,
    propertyId,
    secondarySearchPropertyId,
    metasearchFilters,
    isNearbyStreaming,
    isMetaSearchMobileV2,
    hasListingFilters,
    listSortMode,
  ])

  const [distanceMinRequiredOffers, setDistanceMinRequiredOffers] = useState(prioritySecondaryTileCount)

  useEffect(() => {
    setDistanceMinRequiredOffers(prioritySecondaryTileCount)
  }, [propertyId, metasearchFilters.checkIn, metasearchFilters.checkOut, metasearchFilters.rooms, isNearbyStreaming, prioritySecondaryTileCount])

  const onDistanceLoadMore = useCallback(() => {
    setDistanceMinRequiredOffers(previousMinRequiredOffers => (
      Math.min(previousMinRequiredOffers + METASEARCH_LOAD_MORE_BATCH_SIZE, YMAL_LIMIT)
    ))
  }, [])

  const distanceExcludedIds = useMemo(
    () => [offer?.id, flashOfferId].filter((id): id is string => !!id),
    [offer?.id, flashOfferId],
  )

  const distanceOfferList = useOfferList(distanceSearchFilters, {
    disabled: !isNearbyStreaming || !offer || fetchingPropertyMapping,
    excludedIds: distanceExcludedIds.length > 0 ? distanceExcludedIds : undefined,
    // Start with the first batch and progressively extend while the user scrolls.
    minRequiredOffers: distanceMinRequiredOffers,
    functionScoreWeights: isDistanceMode ? METASEARCH_DISTANCE_FUNCTION_SCORE_WEIGHTS : undefined,
  })
  const distanceOfferListKey = useMemo(
    () => distanceSearchFilters ? getOfferListKey(distanceSearchFilters) : undefined,
    [distanceSearchFilters],
  )
  const distanceOfferMetaData = useAppSelector(state =>
    distanceOfferListKey ? state.offer.searchResultMetadata.offerMetaData[distanceOfferListKey] : undefined,
  )
  const distanceOfferIdsWithoutMatchingProperty = useMemo(
    () => distanceOfferList.offerIds.filter(id => shouldShowNearbyOffer(
      id,
      flashOfferId,
      distanceOfferMetaData?.[id],
    )),
    [distanceOfferList.offerIds, distanceOfferMetaData, flashOfferId],
  )
  const distanceTileDisplayFilters = useMemo(() => ({
    showDistanceWithMetaOfferId: propertyId,
    checkIn: isNearbyStreaming ? metasearchFilters.checkIn : undefined,
    checkOut: isNearbyStreaming ? metasearchFilters.checkOut : undefined,
  }), [propertyId, metasearchFilters.checkIn, metasearchFilters.checkOut, isNearbyStreaming])

  // YMAL secondary tiles always render dateless ("anytime") pricing — the
  // user's incoming dates are not forwarded here.
  const ymalTileFilters = useMemo<App.OfferListFilters>(
    () => buildYmalTileFilters(propertyId),
    [propertyId],
  )

  const sortOptions = useMemo(() => {
    let options = searchType === 'landmark' ? landmarkSearchSortByTypes : searchSortByTypes

    options = [...options, SORT_OPTION_CUSTOMER_RATING]

    return options
  }, [searchType, SORT_OPTION_CUSTOMER_RATING, landmarkSearchSortByTypes, searchSortByTypes])

  const Footer = config.footer.type === 'original' ? OriginalFooter : CompactFooter

  const [showFooter, setShowFooter] = useState<boolean>(false)

  const mapViewText = intl.formatMessage({
    id: 'zPBWnim4YLg_6QKIaKJg',
    defaultMessage: 'Map view',
    description: 'Button text to switch to map view on the map or location view',
  })

  const saveUpToBannerText = isMetaSearchMobileV2 ?
      intl.formatMessage({
        id: 'damviHR0X9mp1qpwppl5',
        defaultMessage: 'Save up to 58% on these nearby offers!',
        description: 'Banner message promoting discounts on nearby meta search offers',
      }) :
      intl.formatMessage({
        id: 'wGlAkHeVO437Q9xej0BW',
        defaultMessage: 'Save up to 58% on these exclusive nearby offers!',
        description: 'Banner message promoting discounts on nearby offers',
      })
  const shouldShowSaveUpToBanner = !flashOfferId

  const recommendedLabel = intl.formatMessage({
    id: 'xCfG-BqN_4hiZ-8TVmBx',
    defaultMessage: 'Recommended',
    description: 'Sort option label for recommended offers on the offer page',
  })

  const closestDistanceLabel = intl.formatMessage({
    id: 'e1S0PCz6_43aKRgLwX7S',
    defaultMessage: 'Closest distance',
    description: 'Sort option label for closest distance on the hotel search page',
  })

  const highestDiscountLabel = intl.formatMessage({
    id: 'culV-HTaN_fmw3wtWjA0',
    defaultMessage: 'Highest discount value',
    description: 'Sort option label for highest discount on the hotel search page',
  })

  const highestRatingLabel = intl.formatMessage({
    id: 'eXCBkesZB5vuChkGyT33',
    defaultMessage: 'Highest rating',
    description: 'Sort option label for highest rating on the hotel search page',
  })

  // V1: `recommendation`; V2 OfferSortControl: `recommended`.
  const metaSearchSortOptions = useMemo(() => [
    { label: recommendedLabel, value: 'recommendation' },
    { label: closestDistanceLabel, value: 'distance' },
    { label: highestDiscountLabel, value: 'discount' },
    { label: highestRatingLabel, value: 'rating' },
  ], [recommendedLabel, closestDistanceLabel, highestDiscountLabel, highestRatingLabel])

  const metaSearchSortControlOptions = useMemo((): Array<OfferListSortOption> => (
    metaSearchSortOptions.map(option => (
      option.value === 'recommendation' ?
          { ...option, value: 'recommended' } :
        option
    ))
  ), [metaSearchSortOptions])

  const sortInputs = metaSearchSortOptions.map(option => <FilterChip
    key={option.value}
    selected={listSortMode === option.value}
    onClick={() => {
      const nextParams = new URLSearchParams(queryParams)
      nextParams.set('sortBy', option.value)
      navigate({ search: nextParams.toString() }, { replace: true })
    }}
    name={option.label}
    value={option.value}
    size="medium"
  >
    {option.label}
  </FilterChip>)

  useEffect(() => {
    dispatch(fetchMetaSearchOfferRates({
      propertyId,
      filters: listFilters,
      source: topOfferSource,
      regionCode: region,
      offerListKey,
      alternativeSourceOfferListKey: offerListKey,
    }))
  }, [dispatch, listFilters, offerListKey, propertyId, region, topOfferSource])

  useEffect(() => {
    dispatch(fetchMetasearchDiscount({
      token: gpid,
      propertyId,
      checkIn: listFilters.checkIn,
      checkOut: listFilters.checkOut,
      price: topOfferPrice,
      propertyFees: topOfferPricing?.propertyFees,
      occupancy: discountOccupancy,
      currency,
      mx: topOfferPricing?.mx,
      rateRuleId,
    }))
  }, [currency, discountOccupancy, dispatch, gpid, listFilters.checkIn, listFilters.checkOut, propertyId, rateRuleId, topOfferPrice, topOfferPricing?.propertyFees, topOfferPricing?.mx])

  useEffect(() => {
    trackClientEvent({
      subject: listSortMode,
      action: 'sort-assigned',
      category: 'metasearch',
      type: 'nonInteraction',
    })
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!fetching && !offer) {
      const paramObject = Object.fromEntries(queryParams.entries())
      generalLog({
        status: 'error',
        message: 'MetaSearchPage: No results found',
        payload: {
          ...paramObject,
          propertyId,
          fullPath: pathName + windowSearch,
        },
      })
    }
  }, [fetching, offer, pathName, propertyId, queryParams, windowSearch])

  useEffect(() => {
    if (!fetching && offer) {
      setShowFooter(true)
    }
  }, [fetching, offer, isMobile])

  // Detect destination changes when search is submitted
  useEffect(() => {
    const hasDestinationChanged =
      !!params.destinationId ||
      !!params.landmarkId ||
      !!params.query ||
      (!!params.propertyId && !params.propertyId.endsWith(propertyId))

    if (hasDestinationChanged) {
      navigate({
        pathname: `/${region}/search`,
        search: windowSearch,
      }, { replace: true })
    }
  }, [
    navigate,
    region,
    params.destinationId,
    params.destinationName,
    params.propertyName,
    params.propertyId,
    params.landmarkId,
    params.landmarkName,
    params.query,
    propertyId,
    pathName,
    windowSearch,
  ])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'auto', top: 0 })
  }, [dispatch])

  const offerListFiltersProps = {
    sortOptions,
    searchType,
    switchView: {
      label: mapViewText,
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  // Initiate tracking for the meta search page
  const tracking = useOfferListTracking(
    getListName('MetaSearch', 'MetaSearch', 'Meta Search Page'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad) {
      trackEvent(searchEvent('offer-list-load', 'hotels-search-page', {
        filters: metasearchFilters,
        searchItem: searchParams.searchItem,
        checkIn: metasearchFilters.checkIn,
        checkOut: metasearchFilters.checkOut,
        vertical: 'hotels',
        occupancies: metasearchFilters.rooms,
      }))
    }
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer, position, price, memberPrice, duration, extendedAvailability, availability, isRightClick } = dispatchAction
      trackEvent(
        productClickEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, isRightClick },
        ),
      )
      if (!isRightClick) {
        trackClientEvent({
          subject: 'metasearch-offer-clicked',
          action: 'click',
          category: 'logging',
          type: 'interaction',
          optimizelyEventId: '4992635119599616',
          optimizelyEventKey: 'metasearch_offer_click',
        })
        if (extendedAvailability) {
          trackClientEvent({
            subject: 'clicked',
            action: `${extendedAvailability}`,
            category: 'extended-window',
            type: 'interaction',
          })
        }
      }
    } else if (dispatchAction.type === OfferListEvents.productImpression) {
      const { offer, position, availability, price, memberPrice, duration, extendedAvailability, roomRateId } = dispatchAction
      trackEvent(
        impressionEventWithContext(
          position + 1,
          tracking.listName,
          tracking.listId,
          offer,
          { productAvailable: availability, price, memberPrice, duration, roomRateId },
        ),
      )
      if (extendedAvailability) {
        trackClientEvent({
          subject: 'viewed',
          action: `${extendedAvailability}`,
          category: 'extended-window',
          type: 'interaction',
        })
      }
    }
  }, [tracking.listName, tracking.listId, metasearchFilters, searchParams.searchItem])

  if (!offer && !fetching) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  const shouldShowDistanceLoadingTiles = isNearbyStreaming &&
    !!distanceSearchFilters &&
    distanceOfferList.offerIds.length === 0 &&
    (
      distanceOfferList.fetching ||
      (
        distanceOfferList.socketListenersStatus === 'listening' &&
        !distanceOfferList.streamEnded
      )
    )

  // Render the flash tile unconditionally when known so it stays visible
  // through the streamed distance list's loading window — keeps slot 0
  // mounted across the skeleton→stream transition (stable React key).
  const flashDistanceTile = flashOfferId ? (
    <OfferListOfferTile
      key={flashOfferId}
      offerId={flashOfferId}
      position={0}
      eagerLoadImage
      lazyLoad="none"
      tileStyle="search"
      wrapperClass="offer-tile"
      filters={distanceTileDisplayFilters}
      metaSearchResult="secondary"
      showPerNightPricing={!isSearchTileDensityExperimentCompact && !!shouldShowPerNightPricing}
      searchTileDensity={secondarySearchTileDensity}
    />
  ) : null

  const distanceTilesBelow = shouldShowDistanceLoadingTiles
    ? Array.from({ length: Math.max(prioritySecondaryTileCount - (flashDistanceTile ? 1 : 0), 0) }, (_, index) => (
        <OfferListLoadingOfferTile
          key={`distance-loading-${index}`}
          tileStyle="search"
          className="offer-tile"
          filters={distanceSearchFilters}
          offerPosition={flashDistanceTile ? index + 1 : index}
          searchTileDensity={secondarySearchTileDensity}
        />
      ))
    : distanceOfferIdsWithoutMatchingProperty
        .filter(id => flashDistanceTile ? id !== flashOfferId : true)
        .slice(0, Math.max(distanceMinRequiredOffers - (flashDistanceTile ? 1 : 0), 0))
        .map((offerId, index) => {
          const positionInList = flashDistanceTile ? index + 1 : index
          const shouldLoadImmediately = positionInList < prioritySecondaryTileCount
          const offerMetaData = distanceOfferMetaData?.[offerId]
          const tileFilters = offerMetaData?.isMatchingProperty && isFlashHotelType(offerMetaData.type as App.OfferType | undefined) ?
            distanceTileDisplayFilters :
            distanceSearchFilters

          return <OfferListOfferTile
            key={offerId}
            offerId={offerId}
            position={positionInList}
            eagerLoadImage={positionInList === 0}
            lazyLoad={shouldLoadImmediately ? 'none' : 'offer-and-inview'}
            tileStyle="search"
            wrapperClass="offer-tile"
            filters={tileFilters}
            metaSearchResult="secondary"
            showPerNightPricing={!isSearchTileDensityExperimentCompact && !!shouldShowPerNightPricing}
            searchTileDensity={secondarySearchTileDensity}
          />
        })

  const distanceTiles = flashDistanceTile
    ? [flashDistanceTile, ...distanceTilesBelow]
    : distanceTilesBelow
  const shouldRenderDistanceTiles = isNearbyStreaming && (
    shouldShowDistanceLoadingTiles ||
    distanceOfferIdsWithoutMatchingProperty.length > 0 ||
    !distanceOfferList.streamEnded
  )

  const shouldShowYmalLoadingTiles = !shouldRenderDistanceTiles &&
    metaSearchOfferList.length === 0 &&
    (
      fetching ||
      metaSearchOfferListFetching
    )

  const ymalTiles = shouldShowYmalLoadingTiles
    ? Array.from({ length: prioritySecondaryTileCount }, (_, index) => (
        <OfferListLoadingOfferTile
          key={`ymal-loading-${index}`}
          tileStyle="search"
          className="offer-tile"
          filters={{ showDistanceWithMetaOfferId: propertyId }}
          offerPosition={index}
          searchTileDensity={secondarySearchTileDensity}
        />
      ))
    : take(metaSearchOfferList, ymalTileCount).map(({ offerId, accommodationId }, index) => {
        const shouldLoadImmediately = index < prioritySecondaryTileCount

        return (
          <OfferListOfferTile
            key={offerId}
            offerId={offerId}
            position={index}
            eagerLoadImage={index === 0}
            lazyLoad={shouldLoadImmediately ? 'none' : 'offer-and-inview'}
            tileStyle="search"
            wrapperClass="offer-tile"
            filters={ymalTileFilters}
            metaSearchResult="secondary"
            accommodationId={accommodationId}
            showPerNightPricing={!isSearchTileDensityExperimentCompact && !!shouldShowPerNightPricing}
            searchTileDensity={secondarySearchTileDensity}
          />
        )
      })

  // Shared by the V1 and V2 headers so the two variants can never drift on
  // search behaviour — only on presentation.
  const searchHeaderProps = {
    filters,
    formRef,
    offerListFiltersProps,
    searchVertical: 'hotels' as const,
    baseSearchPath: pathName,
    baseSearchMapPath: '/search/map',
    onFilterChange: handleFilterChange,
    onSortChange: handleSortChange,
    showFilters: false,
  }

  const searchHeaderElement = isMetaSearchMobileV2 ?
      <MetaSearchV2Header
        {...searchHeaderProps}
        filterRow={{
          filters: metasearchFilters,
          sortOptions: metaSearchSortControlOptions,
          mapViewUrl: mapViewURL,
          onSortChange: handleSortChange,
        }}
      /> :
      <HotelSearchHeader {...searchHeaderProps} />

  const v1MobileSortRow = <LayoutContainer>
    <HorizontalContainerWithFadedOverflow gap={8}>
      {sortInputs}
    </HorizontalContainerWithFadedOverflow>
  </LayoutContainer>

  // Shared by the V1 and V2 offer lists — identical tiles mean identical
  // impression/click tracking across variants.
  const offerListContent = <>
    <CSSBreakpoint min="tablet">
      <Group direction="horizontal" horizontalAlign="space-between">
        <HorizontalContainerWithFadedOverflow gap={8}>
          {sortInputs}
        </HorizontalContainerWithFadedOverflow>
        <TextButton
          kind="secondary"
          variant="default"
          to={mapViewURL}
          startIcon={<LineMapIcon />}
        >
          {mapViewText}
        </TextButton>
      </Group>
    </CSSBreakpoint>
    <OfferListOfferTile
      offerId={propertyId}
      position={0}
      eagerLoadImage
      tileStyle="search"
      wrapperClass="offer-tile"
      filters={listFilters}
      metaSearchResult="primary"
      searchTileDensity={searchTileDensity}
      searchTileTreatment={mainOfferSearchTileTreatment}
    />
    {/* The Google Hotel Ads verifier receives only the primary offer. */}
    {!isGoogleHotelAdsVerifier && <>
      {shouldShowSaveUpToBanner && (
        isMetaSearchMobileV2 ? (
          <MetaSearchV2SaveUpToBanner kind="promotional" density="compact" icon={<LineInfoCircleIcon />}>
            {saveUpToBannerText}
          </MetaSearchV2SaveUpToBanner>
        ) : (
          <MessageBanner kind="promotional" icon={<LineInfoCircleIcon />}>
            {saveUpToBannerText}
          </MessageBanner>
        )
      )}
      {shouldRenderDistanceTiles ? distanceTiles : ymalTiles}
      {!shouldRenderDistanceTiles && metaSearchOfferList.length > ymalTileCount && (
        <MetaSearchLoadMoreButton
          onClick={onYmalLoadMore}
          onScrollNear={onYmalLoadMore}
          size="large"
        >
          {intl.formatMessage({
            id: 'NbcY1R5kaZWI1q8j3nio',
            defaultMessage: 'Load more',
            description: 'Action button label to load more offers in the offer list',
          })}
        </MetaSearchLoadMoreButton>
      )}
      {shouldRenderDistanceTiles && !distanceOfferList.streamEnded && distanceMinRequiredOffers < YMAL_LIMIT && (
        <MetaSearchLoadMoreButton
          onClick={onDistanceLoadMore}
          onScrollNear={onDistanceLoadMore}
          showLoadingIndicator
          size="large"
        >
          {intl.formatMessage({
            id: 'NbcY1R5kaZWI1q8j3nio',
            defaultMessage: 'Load more',
            description: 'Action button label to load more offers in the offer list',
          })}
        </MetaSearchLoadMoreButton>
      )}
    </>}
  </>

  return <MetaSearchMobileV2Context value={isMetaSearchMobileV2}>
    <PageWrapper direction="vertical" verticalAlign="space-between" gap={16} className={clsx({ v2: isMetaSearchMobileV2 })}>
      <div>
        <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
          <GlobalSearchContextProvider
            state={globalHotelsSearchState}
            dispatch={globalHotelsSearchDispatch}
          >
            { offer && <MetaSearchPageHeadData offer={offer as App.BedbankOffer} searchParams={searchParams} filters={{ ...listFilters }} /> }
            { !isGoogleHotelAdsVerifier && ymalListKey && <MetaSearchPageWarmupHints ymalListKey={ymalListKey} maxOfferIds={prioritySecondaryTileCount} /> }
            {/* V2 sticky header must stay outside a short Group or it unpins early. */}
            {isMetaSearchMobileV2 ? (
              !hideSearchHeader && searchHeaderElement
            ) : (
              <Group direction="vertical" gap={16}>
                {!hideSearchHeader && searchHeaderElement}
                <CSSBreakpoint max="mobile">
                  {v1MobileSortRow}
                </CSSBreakpoint>
              </Group>
            )}
            <VoucherDisplayConfig />
            <StyledLayoutContainer
              size="xl"
              className={clsx({ fullWidth: isMobile || isMetaSearchMobileV2, v2: isMetaSearchMobileV2 })}
            >
              <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                <StyledVerticalSpacer className={clsx({ v2: isMetaSearchMobileV2 })} gap={isMetaSearchMobileV2 ? 12 : 16}>{offerListContent}</StyledVerticalSpacer>
              </OfferListEventsProvider>
            </StyledLayoutContainer>
            {isMetaSearchMobileV2 && (
              <OfferListFilterSidebar
                open={isFilterPanelOpen}
                onClose={toggleFilterPanel}
                filters={metasearchFilters}
                sortOptions={metaSearchSortControlOptions}
                search={windowSearch}
                enabledFilters={enabledFilters}
                onFilterChange={handleFilterChange}
              />
            )}
          </GlobalSearchContextProvider>
        </AnalyticsPageContext.Provider>
      </div>
      {showFooter && <Footer />}
    </PageWrapper>
  </MetaSearchMobileV2Context>
}

export default connector(MetaSearchPage)
